import React from 'react'
import { motion } from 'framer-motion'
import { 
  Heart, 
  Users, 
  Shield, 
  Home, 
  Star, 
  Quote,
  Award,
  Calendar,
  MapPin,
  Phone,
  Mail
} from 'lucide-react'

const About = () => {
  const values = [
    {
      icon: Users,
      title: 'Inclusion Sociale',
      description: 'Nos intervenants de plancher développent quotidiennement des activités avec nos résidents en vue d\'accroître leur autonomie fonctionnelle.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Heart,
      title: 'Recherche d\'Autonomie',
      description: 'Développement de l\'autonomie des personnes en déficience intellectuelle par des activités adaptées et personnalisées.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Shield,
      title: 'Garde Adaptée et Responsive',
      description: 'Les jeunes adultes aux besoins particuliers nécessitent une garde adaptée et personnalisée.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Home,
      title: 'Environnement Sécuritaire',
      description: 'Un cadre de vie sécuritaire qui respecte les principes fondamentaux d\'hygiène et sanitaires.',
      color: 'from-orange-500 to-red-500',
    },
  ]

  const achievements = [
    { icon: Users, value: '60+', label: 'Résidents' },
    { icon: Home, value: '25+', label: 'Chambres' },
    { icon: Star, value: '4.9', label: 'Note' },
    { icon: Award, value: '15+', label: 'Années' },
  ]

  const timeline = [
    {
      year: '2004',
      title: 'Début du Parcours',
      description: 'Stéphanne Masséna commence à aider sa mère dans la gestion de personnes en situation de déficience intellectuelle.',
      icon: Heart
    },
    {
      year: '2010',
      title: 'Première Résidence',
      description: 'Création de la première résidence sous le label Résidences Masséna à Laval.',
      icon: Home
    },
    {
      year: '2015',
      title: 'Expansion',
      description: 'Ouverture de la deuxième résidence et développement des services d\'accompagnement.',
      icon: Users
    },
    {
      year: '2024',
      title: 'Reconnaissance',
      description: 'Trois résidences actives avec une équipe professionnelle dédiée au bien-être des résidents.',
      icon: Award
    }
  ]

  return (
    <div className="min-h-screen pt-32 pb-20 bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-primary-100" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-primary-600 font-semibold uppercase tracking-wider mb-4"
            >
              AU SERVICE DE L'AUTONOMIE ET DU BIEN-ÊTRE
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-5xl md:text-6xl font-bold text-black mb-6"
            >
              À Propos de Nous
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            >
              Depuis près de deux décennies, les Résidences Masséna Inc. se consacrent à l'accompagnement des jeunes adultes ayant une déficience intellectuelle, en créant des environnements sécuritaires et harmonieux qui favorisent leur autonomie et leur bien-être.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {achievements.map((achievement, index) => {
            const Icon = achievement.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-primary-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                  <Icon className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-black mb-2">
                    {achievement.value}
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {achievement.label}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>

      {/* Mission & Vision Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl shadow-lg"
          >
            <div className="text-center">
              <Heart className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-green-800 mb-6">
                Notre Mission
              </h2>
              <p className="text-green-700 text-lg leading-relaxed font-medium">
                ACCROÎTRE L'AUTONOMIE DES PERSONNES ADULTES EN DÉFICIENCES INTELLECTUELLES ET EN TROUBLE DU SPECTRE DE L'AUTISME
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl shadow-lg"
          >
            <div className="text-center">
              <Home className="w-16 h-16 text-blue-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-blue-800 mb-6">
                Notre Vision
              </h2>
              <p className="text-blue-700 text-lg leading-relaxed font-medium">
                ACCORDER UN NOUVEAU "CHEZ-SOI" AUX USAGERS AVEC UN ENVIRONNEMENT SÉCURITAIRE, HARMONIEUX, CONVIVIAL ET QUI RESPECTE LES PRINCIPES FONDAMENTAUX D'HYGIÈNE ET SANITAIRES
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Founder Section */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-black mb-4">
              Notre Fondatrice
            </h2>
            <p className="text-xl text-gray-600">
              Stéphanne Masséna
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Founder Photo */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://raw.githubusercontent.com/BrandBoostApp/image-/main/Massena.jpg" 
                alt="Stéphanne Masséna - Fondatrice"
                className="w-full h-auto max-w-md mx-auto"
              />
            </motion.div>

            {/* Founder Story */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">
                  Stéphanne Masséna
                </h3>
                <p className="text-primary-600 font-semibold mb-6">
                  Fondatrice des Résidences Masséna Inc.
                </p>
                <Quote className="w-8 h-8 text-primary-500 mb-4" />
                <blockquote className="text-gray-600 text-lg italic leading-relaxed mb-6">
                  "Depuis près de deux décennies, j'ai consacré ma vie à accompagner les jeunes adultes ayant une déficience intellectuelle. Tout a commencé en aidant ma mère à gérer des personnes en situation de déficience intellectuelle. Cette expérience m'a profondément marquée et m'a donné la passion de créer des établissements sous le label Résidences Masséna, dédiés au bien-être et à l'autonomie de ces personnes extraordinaires."
                </blockquote>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* History Timeline */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-black mb-4">
            Notre Histoire
          </h2>
          <p className="text-xl text-gray-600">
            Un parcours de près de deux décennies dédié au bien-être
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary-500 to-primary-600 rounded-full" />
          
          <div className="space-y-12">
            {timeline.map((item, index) => {
              const Icon = item.icon
              const isLeft = index % 2 === 0
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex items-center ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${isLeft ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                      <div className="flex items-center mb-4">
                        <div className="bg-primary-100 p-3 rounded-full mr-4">
                          <Icon className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-primary-600">{item.year}</div>
                          <h3 className="text-xl font-bold text-black">{item.title}</h3>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex-shrink-0 w-4 h-4 bg-primary-500 rounded-full border-4 border-white shadow-lg" />
                  
                  <div className="w-1/2" />
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-black mb-4">
              Nos Valeurs
            </h2>
            <p className="text-xl text-gray-600">
              Les principes qui guident notre mission
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="text-center">
                    <div className={`bg-gradient-to-br ${value.color} p-4 rounded-2xl inline-flex mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-black mb-4">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary-500 to-primary-600 p-12 rounded-3xl text-center text-white shadow-2xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Prêt à Nous Rencontrer ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Découvrez comment nous pouvons accompagner votre proche
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              Nous Contacter
            </motion.a>
            <motion.a
              href="/appointment"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-primary-600 transition-colors"
            >
              Prendre un Rendez-vous
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default About